// Importaciones principales
import {
  Alert,
  Card,
  CardContent,
  Snackbar,
  useTheme,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import SelectMultipleWidgetFilter from "components/SelectMultipleWidgetFilter";
import SwitchController from "components/SwitchController";
import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import processAsigned from "../../../common/processAssigned.json";
import viewsTexts from "../../../common/viewsTexts.json";
import {
  BreadCrumbs,
  CardHeaderWidget,
  RadioGroupWidget,
  SaveAction,
  SnackBarsComponent,
} from "../../../components";
import { FiltersContext } from "../../../context";
import { AxiosContext } from "../../../context/AxiosContext";
import { Context } from "../../../context/Context";
import { alphabeticalSort } from "../../../helpers";
import { BsCol, BsContainer, BsRow } from "../../../layouts/components";

// Estilos personalizados
const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.tertiary.main,
    fontWeight: 500,
  },
  divider: {
    background: theme.palette.grey[400],
    marginTop: 10,
  },
  rootHelper: {
    fontSize: 17,
  },
  inputText: {
    fontSize: 26,
  },
}));

// IDs de perfiles activos permitidos
const profilesActives = [
  "6442acd4c818b181aaf9b958",
  "6422e06926bae345b6309ab5",
  "6422e08d26bae345b6309abb",
  "66b35738fce7da894e2b2572",
  "6440365f8ff3cd780b696243",
];

const UserPrivilegeMasive = (props) => {
  // Contextos globales
  const { userId, userHuerto, userName, userOrchard } = useContext(Context);
  const { state: filterState } = useContext(FiltersContext);
  const axiosContext = useContext(AxiosContext);
  const theme = useTheme();
  const classes = useStyles();

  // -----------------------------
  // Estados locales del componente
  // -----------------------------
  const [filterDataResponseState, setFilterDataResponseState] = useState(
    filterState.userOrchard_filter
  );
  const [loadingQueryState, setLoadingQueryState] = useState(false);
  const [successQueryState, setSuccessQueryState] = useState(false);
  const [loadingProfileDataState, setLoadingProfileDataState] = useState(false);
  const [loadingModuleDataState, setLoadingModuleDataState] = useState(false);
  const [loadingClientDataState, setLoadingClientDataState] = useState(false);
  const [profileDataState, setProfileDataState] = useState([]);
  const [clientDataState, setClientDataState] = useState([]);
  const [moduleDataState, setModuleDataState] = useState([]);
  const [processDataState, setProcessDataState] = useState([]);
  const [errorState, setErrorState] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  // -----------------------------
  // React Hook Form
  // -----------------------------
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      radioProcess: true,
      radioWithProcess: false,
    },
  });

  // -----------------------------
  // Variables de formulario
  // -----------------------------
  const watchClient = watch("client");
  const watchProfile = watch("profile");
  const useRadioWithProcess = watch("radioWithProcess");
  const useRadioProcess = watch("radioProcess");
  const useModule = watch("module");
  const useProcess = watch("process", []);

  // -----------------------------
  // useEffect para limpiar procesos si se limpia el módulo
  // -----------------------------

  useEffect(() => {
    if (
      !useModule ||
      (useModule && useModule.id !== processDataState?.[0]?.moduleId)
    ) {
      setValue("process", []);
      setProcessDataState([]);
    }
  }, [useModule, setValue]);

  useEffect(() => {
    if (useRadioWithProcess === false) {
      setValue("process", []);
      setProcessDataState([]);
      setValue("module", null);
    }
  }, [useRadioWithProcess]);

  // -----------------------------
  // Función principal de envío de formulario
  // -----------------------------
  const onSubmit = async (values) => {
    setSuccessQueryState(false);
    setLoadingQueryState(true);
    // console.log(values, "<-----values");
    // console.log(values.profile, "<-----values.profile");
    const profileSelected = profileDataState.filter(
      (ele) => ele.value === values?.profile?.value
    );
    // Convierte el string a booleano
    const statusAdd =
      values.radioProcess === "true" || values.radioProcess === true;
    const processFormatted = values?.process?.map((ele) => ele.id);
    // console.log(processFormatted, "<---- processFormatted");
    // console.log(useProcess, "<---- useProcess");
    // console.log(values, "<---- values");
    const query = {
      profile: profileSelected[0].id,
      clientValue: values?.client?.value,
      statusAdd,
      userProcess: values?.process.length
        ? processFormatted
        : processAsigned[profileSelected[0].id]?.process,
    };
    // setLoadingQueryState(false);

    try {
      const response = await axiosContext.authAxios.post(
        "v1/user/update-process-user",
        query
      );
      if (response.status === 200) {
        setSuccessQueryState(true);

        // Determina la palabra según statusAdd
        const action = statusAdd ? "agregados" : "Eliminados";
        const regex = new RegExp(
          `^Procesos y Módulos ${action} correctamente\\.\\s*`,
          "i"
        );
        const cleanMsg = response.data.updateProcessUser.message.replace(
          regex,
          ""
        );
        setValue("process", []);
        setValue("module", null);
        setResponseMessage(cleanMsg);
      }
      // setLoadingQueryState(false);
    } catch (err) {
      if (err.request.status === 500) {
        setErrorState(true);
      }
    } finally {
      setLoadingQueryState(false);
    }
  };

  // -----------------------------
  // useEffect para cargar datos iniciales
  // -----------------------------
  useEffect(() => {
    getProfilesAsync();
    getClientsAsync();
    getModuleAsync();
  }, []);

  // -----------------------------
  // useEffect para actualizar procesos al seleccionar módulo
  // -----------------------------
  useEffect(() => {
    if (useModule) {
      const moduleSelected = moduleDataState.filter(
        (ele) => ele.id === useModule.id
      );
      const processSelected = moduleSelected[0].process;
      setProcessDataState(alphabeticalSort(processSelected, "label"));
    }
  }, [useModule, useProcess]);

  // -----------------------------
  // Funciones para obtener datos de la API
  // -----------------------------
  const getProfilesAsync = useCallback(async () => {
    setLoadingProfileDataState(true);
    try {
      let response = await axiosContext.authAxios.post(
        "v1/profile/list-profiles"
      );
      const { data, status } = response;
      if (status === 200) {
        let dataProfiles = alphabeticalSort(data.data, "label");
        dataProfiles = dataProfiles.map((profile) => {
          if (profile.value === "Administrador") {
            return {
              ...profile,
              label: `${profile.label} (JTP)`,
            };
          }
          return profile;
        });
        dataProfiles = dataProfiles.filter((profile) =>
          profilesActives.includes(profile.id)
        );
        setProfileDataState(dataProfiles);
      }
    } catch (error) {
      // Manejo de error opcional
    } finally {
      setLoadingProfileDataState(false);
    }
  }, [axiosContext]);

  const getModuleAsync = useCallback(async () => {
    setLoadingModuleDataState(true);
    try {
      let response = await axiosContext.authAxios.get("v1/user/module-process");
      const { data, status } = response;
      if (status === 200) {
        let dataModules = alphabeticalSort(data, "namePrint");
        dataModules = dataModules.map((module) => {
          const updatedProcess = module.process.map((proceso) => ({
            ...proceso,
            label: proceso.namePrint,
            value: proceso.id,
          }));
          return {
            ...module,
            process: updatedProcess,
            label: `${module.namePrint}`,
            value: module.id,
          };
        });
        setModuleDataState(dataModules);
      }
    } catch (error) {
      // Manejo de error opcional
    } finally {
      setLoadingModuleDataState(false);
    }
  }, [axiosContext]);

  const getClientsAsync = useCallback(async () => {
    setLoadingClientDataState(true);
    try {
      let response = await axiosContext.authAxios.post(
        "v1/client/list-clients",
        { limit: 500 }
      );
      const { data, status } = response;
      if (status === 200) {
        setClientDataState(alphabeticalSort(data.data, "label"));
      }
    } catch (error) {
      // Manejo de error opcional
    } finally {
      setLoadingClientDataState(false);
    }
  }, [axiosContext]);

  // -----------------------------
  // Funciones auxiliares para Snackbars
  // -----------------------------
  const handleCloseSnackBarsComponent = () => {
    setErrorState(false);
  };

  const onCloseSnack = () => {
    setSuccessQueryState(false);
  };

  // -----------------------------
  // Renderizado del componente
  // -----------------------------
  return (
    <div className="form-style">
      <BsContainer fluid class="px-0 px-md-3">
        <BsRow class="no-gutters">
          <BsCol class="col-md-12">
            <Card elevation={0}>
              <CardHeaderWidget
                title={viewsTexts.titles.user.userTextPrivilegeMasive}
                subheader={<BreadCrumbs subTitle="" />}
              />
              <CardContent>
                <BsRow>
                  <BsCol class="col-md-12">
                    <form autoComplete="on" onSubmit={handleSubmit(onSubmit)}>
                      <Typography className="font-semibold mb-3">
                        Filtros:
                      </Typography>
                      <BsRow>
                        <BsCol class="col-md-6 mb-3 md:mb-0">
                          <SelectMultipleWidgetFilter
                            errors={errors.client}
                            loading={loadingClientDataState}
                            control={control}
                            labeltext="Cliente"
                            name="client"
                            placeholder="Seleccionar Cliente"
                            display="vertical"
                            variant="outlined"
                            minheight={false}
                            isClearable={true}
                            isMulti={false}
                            isSearchable
                            req={true}
                            onlyOptionValue={false}
                            defaultValue=""
                            options={clientDataState}
                            sizeSelect={"large"}
                          />
                        </BsCol>
                        <BsCol class="col-md-6">
                          <SelectMultipleWidgetFilter
                            errors={errors.profile}
                            loading={loadingProfileDataState}
                            control={control}
                            labeltext="Perfil"
                            name="profile"
                            placeholder="Seleccionar Perfil"
                            display="vertical"
                            variant="outlined"
                            minheight={false}
                            isClearable={true}
                            isMulti={false}
                            isSearchable
                            req={true}
                            onlyOptionValue={false}
                            defaultValue=""
                            options={profileDataState}
                            sizeSelect={"large"}
                          />
                        </BsCol>
                      </BsRow>
                      <Typography className="font-semibold mb-3 mt-3">
                        Procesos a agregar:
                      </Typography>
                      <BsRow class="mt-2">
                        <BsCol class="col-md-6 mb-3 md:mb-0">
                          <SelectMultipleWidgetFilter
                            errors={errors.module}
                            loading={loadingModuleDataState}
                            control={control}
                            labeltext="Módulos"
                            name="module"
                            placeholder="Seleccionar Módulos"
                            display="vertical"
                            variant="outlined"
                            minheight={false}
                            isClearable={true}
                            isMulti={false}
                            isSearchable
                            req={true}
                            onlyOptionValue={false}
                            defaultValue=""
                            options={moduleDataState}
                            sizeSelect={"large"}
                          />
                        </BsCol>
                        <BsCol class="col-md-6">
                          <SelectMultipleWidgetFilter
                            errors={errors.process}
                            loading={loadingModuleDataState}
                            control={control}
                            labeltext="Procesos"
                            name="process"
                            placeholder="Seleccionar Procesos"
                            display="vertical"
                            variant="outlined"
                            minheight={false}
                            isClearable={true}
                            isMulti={true}
                            isSearchable
                            req={true}
                            onlyOptionValue={false}
                            defaultValue=""
                            options={processDataState}
                            sizeSelect={"large"}
                          />
                        </BsCol>
                      </BsRow>
                      <BsRow class="mt-3">
                        <BsCol class="col-md-6">
                          <Typography className="font-semibold">
                            Permisos:
                          </Typography>
                          <RadioGroupWidget
                            control={control}
                            name="radioProcess"
                            errors={errors.credential}
                            variant="outlined"
                          >
                            <FormControlLabel
                              value={true}
                              control={<Radio />}
                              label="Agregar Permisos"
                            />
                            <FormControlLabel
                              value={false}
                              control={<Radio />}
                              label="Quitar Permisos"
                            />
                          </RadioGroupWidget>
                          {/* <Alert
                            severity={useRadioProcess ? "info" : "warning"}
                            variant="filled"
                          >
                            {useRadioProcess
                              ? "Estas Agregando procesos al usuario"
                              : "Estas Quitando procesos al usuario"}
                          </Alert> */}
                        </BsCol>
                      </BsRow>
                      <BsRow>
                        <div className=" mx-auto mb-4">
                          {successQueryState && (
                            <Alert
                              variant="filled"
                              severity="info"
                              className={`text-white bg-[${theme.palette.primary.main}]`}
                              onClose={() => onCloseSnack()}
                            >
                              <Typography className="text-white font-medium">
                                {responseMessage}
                              </Typography>
                            </Alert>
                          )}
                        </div>
                      </BsRow>
                      <BsRow>
                        <BsCol class="col-md-12 text-center ">
                          <SaveAction
                            title="Guardar"
                            color="secondary"
                            size="large"
                            className="roundedButton ml-md-2"
                            variant="contained"
                            disabled={loadingQueryState}
                            messageType="add"
                            mutationLoading={loadingQueryState}
                            // success={successQueryState}
                            actionClose={onCloseSnack}
                          />
                        </BsCol>
                      </BsRow>
                    </form>
                  </BsCol>
                </BsRow>
                {errorState && (
                  <Snackbar
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    open={errorState}
                    onClose={() => setErrorState(false)}
                    autoHideDuration={4500}
                    key={Math.random()}
                  >
                    <SnackBarsComponent
                      variant="error"
                      message="No se pudo guardar el registro"
                      handleClose={handleCloseSnackBarsComponent}
                    />
                  </Snackbar>
                )}
              </CardContent>
            </Card>
          </BsCol>
        </BsRow>
      </BsContainer>
    </div>
  );
};

export default UserPrivilegeMasive;
